/**
 * Eenheden: waarin oppervlakte en prijs per oppervlakte op het scherm staan.
 *
 * BRIEF paragraaf 2 noemt eenheden naast de standaard kaartstijl als app-brede
 * voorkeur, en dat is precies wat het is: je zet het één keer onder Instellingen
 * en het geldt op de kaart, in de popup, in de tabel en in het filter. De data
 * zelf blijft metriek - de kolom in de database heet `oppervlakte_m2` en het
 * filter in de URL rekent in m², zodat een gedeelde link bij iedereen dezelfde
 * woningen oplevert, ongeacht wie welke eenheid heeft staan.
 */
import { formatAantal, formatEuro } from '@mamimap/shared';

export const EENHEDEN = ['m2', 'ft2'] as const;
export type Eenheid = (typeof EENHEDEN)[number];

/** Wat er in het bedieningselement staat. */
export const EENHEID_LABELS: Record<Eenheid, string> = {
  m2: 'Vierkante meter',
  ft2: 'Vierkante voet',
};

/** Het teken dat achter een getal komt, en in een kolomkop. */
export const EENHEID_TEKEN: Record<Eenheid, string> = {
  m2: 'm²',
  ft2: 'ft²',
};

/** De internationale voet is exact 0,3048 m, dus dit getal is exact. */
export const FT2_PER_M2 = 1 / 0.3048 ** 2;

/** Van de opgeslagen m² naar de eenheid die op het scherm staat. */
export function vanM2(m2: number, eenheid: Eenheid): number {
  return eenheid === 'm2' ? m2 : Math.round(m2 * FT2_PER_M2);
}

/** Terug: wat iemand intikt, naar de m² waarin het filter en de database rekenen. */
export function naarM2(waarde: number, eenheid: Eenheid): number {
  return eenheid === 'm2' ? waarde : Math.round(waarde / FT2_PER_M2);
}

/** Hetzelfde, maar voor een veld dat leeg mag zijn. */
export function vanM2Optioneel(
  m2: number | null | undefined,
  eenheid: Eenheid,
): number | undefined {
  if (m2 == null || !Number.isFinite(m2)) return undefined;
  return vanM2(m2, eenheid);
}

export function naarM2Optioneel(
  waarde: number | null | undefined,
  eenheid: Eenheid,
): number | undefined {
  if (waarde == null || !Number.isFinite(waarde)) return undefined;
  return naarM2(waarde, eenheid);
}

/** "84" -> "84 m²" of "904 ft²". */
export function formatMaat(m2: number | null | undefined, eenheid: Eenheid): string {
  if (m2 == null || !Number.isFinite(m2)) return '–';
  return `${formatAantal(vanM2(m2, eenheid))} ${EENHEID_TEKEN[eenheid]}`;
}

/** "8123" -> "€ 8.123/m²" of "€ 755/ft²". */
export function formatPrijsPerMaat(
  prijsPerM2: number | null | undefined,
  eenheid: Eenheid,
): string {
  if (prijsPerM2 == null || !Number.isFinite(prijsPerM2)) return '–';
  return `${formatEuro(prijsPerEenheid(prijsPerM2, eenheid))}/${EENHEID_TEKEN[eenheid]}`;
}

/**
 * Prijs per eenheid gaat de andere kant op dan een oppervlakte: een vierkante
 * voet is kleiner dan een vierkante meter, dus kost hij minder.
 */
export function prijsPerEenheid(prijsPerM2: number, eenheid: Eenheid): number {
  return eenheid === 'm2' ? prijsPerM2 : Math.round(prijsPerM2 / FT2_PER_M2);
}

/** '€/m²' of '€/ft²' - voor een kolomkop of het label van een cijfer. */
export function prijsPerTeken(eenheid: Eenheid): string {
  return `€/${EENHEID_TEKEN[eenheid]}`;
}
